import { selector } from 'recoil';
import { type CallMeVariant } from '../types/opcTypes';
import { opcInfoAtom } from '../atoms/opcInfoAtom';
import { getCoverableInfoSelector } from './getCoverableInfoSelector';
import {
    ACC_DEATH_COVERAGE,
    ACC_HIGH_RISK_SPORT_COVERAGE,
    ACC_IMMEDIATE_COMPENSATION_COVERAGE,
    MVPLEASINGCARRETURN,
    PET_HEALTH_COVERAGE,
    PET_HEALTH_PLUS_COVERAGE,
    PET_LIFE_COVERAGE,
    UVP_ROAD_ASSISTANCE_COV
} from '../../constants/coverage';
import { jobAtom } from '../atoms/jobAtom';
import { jobInfoAtom } from '../atoms/jobInfoAtom';
import { OnlineSales } from 'gw-edge-client';
import { topConfigContextAtom } from '../atoms/topConfigContextAtom';
import { getPrimaryAccountNumber } from '../../utils/topConfigContextUtil';

const getPetCallMeVariant = (coverageId: string): CallMeVariant => {
    if (coverageId === PET_HEALTH_COVERAGE) return 'petBetween5and7YearsOldHealth';
    if (coverageId === PET_HEALTH_PLUS_COVERAGE) return 'petBetween5and7YearsOldHealthPlus';
    return 'petBetween5and7YearsOldLife';
};

export const callMeVariantOpcSelector = selector<CallMeVariant>({
    key: 'callMeVariantOpcSelector',
    get: async ({ get }) => {
        const { productName, coverageId, tilpas, attribute, attributeValue } = get(opcInfoAtom);
        const { isOpcJob } = get(jobAtom);
        const { jobNumber, sessionId } = get(jobInfoAtom);
        const topConfigContext = get(topConfigContextAtom);
        const isLoggedOn = topConfigContext?.user?.isLoggedOn ?? false;
        const accountNumber = getPrimaryAccountNumber(topConfigContext);

        const customerStop = isLoggedOn
            ? await OnlineSales.getVIDCustomerStop(jobNumber, accountNumber, sessionId)
            : { stopCause: 'None' };

        if (customerStop.stopCause !== 'None') {
            return 'backToPreviousPageModal';
        }

        if (attribute === 'prcGoldSilverJewelrySums') {
            return 'prcInsuraceSumsTooLow';
        }

        const {
            isAccAboutToTurn18Or28YearsOld,
            isPRCAboutToTurn28YearsOld,
            isAdultEmploymentProfessionelSport,
            isAccHighRiskORImmediateCompSelected = false,
            isPetBetween5And7YearsOld = false,
            isPRCVoluntaryAlarmNo,
            containsSelectedForceCoverage
        } = get(getCoverableInfoSelector);

        if (tilpas === 'tilfoej') {
            if (productName === 'top_uvp_trailer' && coverageId === UVP_ROAD_ASSISTANCE_COV) {
                return 'default';
            }

            if (coverageId === MVPLEASINGCARRETURN) {
                return 'addLeasingCarReturnCov';
            }

            if (coverageId === ACC_DEATH_COVERAGE) {
                return 'addDeathCov';
            }

            if (
                (productName === 'top_acc_accident' || productName === 'top_acc_young') &&
                (coverageId === ACC_HIGH_RISK_SPORT_COVERAGE ||
                    coverageId === ACC_IMMEDIATE_COMPENSATION_COVERAGE) &&
                isAccHighRiskORImmediateCompSelected
            ) {
                return 'addHighRiskOrImmediateCompCov';
            }

            if (
                (productName === 'topdanmark_dog' || productName === 'topdanmark_cat') &&
                (coverageId === PET_HEALTH_COVERAGE ||
                    coverageId === PET_HEALTH_PLUS_COVERAGE ||
                    coverageId === PET_LIFE_COVERAGE) &&
                isPetBetween5And7YearsOld &&
                isOpcJob
            ) {
                return getPetCallMeVariant(coverageId);
            }
        }

        if (tilpas === 'attribut') {
            if (attribute === 'uvpStationary') {
                return 'uvpStationary';
            }
            if (isPRCVoluntaryAlarmNo) {
                return 'voluntaryAlarmNoToYes';
            }

            if (attribute === 'traNumberInHousehold') {
                return 'traNumberInHousehold';
            }

            if (attribute === 'traAreaOfDestination' && attributeValue === 'Worldwide') {
                return 'backToPreviousPageModal';
            }
        }

        if (isAccAboutToTurn18Or28YearsOld) {
            return productName === 'top_acc_young'
                ? 'youngTurn28Within40days'
                : 'childTurn18Within40days';
        }

        if (isPRCAboutToTurn28YearsOld) {
            return productName === 'top_prc_youth_content'
                ? 'youthContentTurn28Within40days'
                : undefined;
        }

        if (isAdultEmploymentProfessionelSport && coverageId === ACC_HIGH_RISK_SPORT_COVERAGE) {
            return 'professionalSportEmployment';
        }

        if (
            isAdultEmploymentProfessionelSport &&
            coverageId === ACC_IMMEDIATE_COMPENSATION_COVERAGE
        ) {
            return 'professionalSportEmploymentAddImmediateComp';
        }

        if (tilpas !== 'attribut' && containsSelectedForceCoverage) {
            return 'carForcedDeductible';
        }

        return undefined;
    }
});
